import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ROUTES } from "@routing/routes";
import { useAtomValue } from "jotai";
import type { ColumnDef } from "@tanstack/react-table";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { Input } from "@common/Form/Input";
import { Table } from "@common/Table";
import { Badge, Button, Icon, Title, ErrorState } from "@common/index";
import { ComponentColor, ComponentSize } from "@common/types";
import { useOrganizationApi } from "@/hooks/apiHooks/useOrganizationApi";
import { useMountRead } from "@/hooks/useMountRead";
import {
    organizationsListAtom,
    isFetchingOrganizationsListAtom,
} from "@/store/organizationAtoms";
import {
    DEFAULT_ORGANIZATIONS_QUERY,
    type GetOrganizationsQuery,
    type OrganizationListItem,
} from "@models/organization";
import { CreateClientDrawer } from "./_components/CreateClientDrawer";
import { NewCertBadge } from "./_components/NewCertBadge";
import { PendingClientsBanner } from "./_components/PendingClientsBanner";
import { StatusBadge } from "./_components/StatusBadge";
import { StatusFilterChips } from "./_components/StatusFilterChips";
import { findClientFilter, type ClientFilter } from "./_helpers/clientFilter";
import { parseClientSort, toOrganizationsQuerySort } from "./_helpers/clientSort";
import { formatDate } from "@utils/formatter";

export const Clients = () => {
    const { getOrganizationList } = useOrganizationApi();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const { items, totalPages, count, allCount, hasPreviousPage, hasNextPage } =
        useAtomValue(organizationsListAtom);
    const isFetching = useAtomValue(isFetchingOrganizationsListAtom);

    const currentPage = Number(searchParams.get("page")) || 1;
    const currentPageSize =
        Number(searchParams.get("pageSize")) ||
        DEFAULT_ORGANIZATIONS_QUERY.pageSize!;
    const searchTerm = searchParams.get("search") || "";
    const activeFilter = findClientFilter(searchParams.get("status"));
    // #333: null is the list's own order (Submitted, newest first). Memoized
    // on the two strings, so the read re-issues only when the sort changes.
    const sortByParam = searchParams.get("sortBy");
    const sortDirectionParam = searchParams.get("sortDirection");
    const sort = useMemo(
        () => parseClientSort(sortByParam, sortDirectionParam),
        [sortByParam, sortDirectionParam],
    );

    // X2: the banner's count comes from THIS mount's own reads (#407), never
    // the shared atom, which still holds the last visit's page.
    const [pendingCount, setPendingCount] = useState<number>();

    // C3: the New Client drawer.
    const [isCreateOpen, setIsCreateOpen] = useState(false);

    // Debounce search input to avoid a request per keystroke.
    const [searchQuery, setSearchQuery] = useState<string>(searchTerm);

    // Keep the input in sync when the URL changes outside of typing
    // (browser back/forward, cleared filters). Without this the box keeps
    // stale text and the debounce effect re-pushes it, fighting navigation.
    useEffect(() => {
        setSearchQuery(searchTerm);
    }, [searchTerm]);

    const columns = useMemo<ColumnDef<OrganizationListItem>[]>(
        () => [
            {
                accessorKey: "name",
                header: "Customer",
                enableSorting: false,
                cell: ({ row }) => (
                    <button
                        type="button"
                        className="font-bold text-nowrap text-neutral text-left hover:underline"
                        onClick={() =>
                            navigate(
                                ROUTES.ADMIN_CLIENTS_DETAIL(row.original.id),
                            )
                        }
                    >
                        {row.original.name}
                    </button>
                ),
            },
            {
                accessorKey: "legalBusinessName",
                header: "Legal Business Name",
                enableSorting: false,
                cell: ({ row }) => (
                    <>
                        <div>{row.original.legalBusinessName}</div>
                        {row.original.taxId && (
                            <div className="text-xs text-neutral/60">
                                Tax ID: {row.original.taxId}
                            </div>
                        )}
                    </>
                ),
            },
            {
                accessorKey: "submittedAt",
                header: "Submitted",
                enableSorting: false,
                cell: ({ getValue }) => formatDate(getValue() as string),
            },
            {
                accessorKey: "status",
                header: "Status",
                enableSorting: false,
                // E12-S5 (#240): "New cert" sits beside the status when any
                // location of the client has a certificate awaiting re-review.
                cell: ({ row }) => (
                    <div className="flex flex-wrap items-center gap-1">
                        <StatusBadge status={row.original.status} />
                        {/* C1: the status is the organization's; this
                            sub-badge rolls up its pending locations. */}
                        {row.original.pendingLocationsCount > 0 && (
                            <Badge
                                color={ComponentColor.Warning}
                                size={ComponentSize.XS}
                            >
                                {row.original.pendingLocationsCount} pending
                                {/* Read as "N pending locations", like
                                    the header badge's title. */}
                                <span className="sr-only"> locations</span>
                            </Badge>
                        )}
                        {row.original.hasPendingCertReview && <NewCertBadge />}
                    </div>
                ),
            },
            {
                accessorKey: "lastReviewedAt",
                header: "Last Reviewed",
                enableSorting: false,
                // "—" when no location has been reviewed yet.
                cell: ({ row }) => formatDate(row.original.lastReviewedAt),
            },
            {
                // #333: sorts (its id is its CLIENT_SORT_BY key), most
                // pending first.
                accessorKey: "pendingLocationsCount",
                header: "Awaiting Approval",
                sortDescFirst: true,
            },
            {
                // #434: passed review, waiting for the activation charge.
                accessorKey: "pendingPaymentLocationsCount",
                header: "Waiting for Payment",
                enableSorting: false,
            },
            {
                accessorKey: "locationsCount",
                header: "Locations",
                enableSorting: false,
            },
            {
                // Opens the detail page until the Edit Organization drawer
                // (B6) exists; the mockup draws that drawer over the detail
                // page, not the list.
                id: "actions",
                // Blank on screen, as drawn; named for screen readers.
                header: () => <span className="sr-only">Actions</span>,
                enableSorting: false,
                cell: ({ row }) => (
                    <div className="flex justify-end">
                        <Button
                            to={ROUTES.ADMIN_CLIENTS_DETAIL(row.original.id)}
                            color={ComponentColor.Primary}
                            size={ComponentSize.XS}
                            outline
                            // Every row says "Edit", so the name says which client.
                            aria-label={`Edit ${row.original.name}`}
                        >
                            <Icon name="edit" className="text-sm" />
                            Edit
                        </Button>
                    </div>
                ),
            },
        ],
        [navigate],
    );

    // Write the debounced search term into the URL (resetting pagination).
    useEffect(() => {
        if (searchQuery !== searchTerm) {
            const timer = setTimeout(() => {
                const next = new URLSearchParams(searchParams);
                if (searchQuery) {
                    next.set("search", searchQuery);
                } else {
                    next.delete("search");
                }
                next.delete("page");
                setSearchParams(next);
            }, 500);

            return () => clearTimeout(timer);
        }
    }, [searchQuery, searchTerm, searchParams, setSearchParams]);

    const fetchClients = useCallback(
        async (isCurrent: () => boolean) => {
            const query: GetOrganizationsQuery = {
                ...DEFAULT_ORGANIZATIONS_QUERY,
                pageNumber: currentPage,
                pageSize: currentPageSize,
                search: searchTerm || undefined,
                // "All" sends neither filter (None=0 is never sent).
                ...activeFilter?.query,
                ...toOrganizationsQuerySort(sort),
            };

            const page = await getOrganizationList(query);
            if (isCurrent()) {
                setPendingCount(page.pendingCount);
            }
        },
        [
            currentPage,
            currentPageSize,
            searchTerm,
            activeFilter,
            sort,
            getOrganizationList,
        ],
    );

    // Issued on mount and on every query change; the page shows a failure
    // only for its own read (#407).
    const { readStatus, reload } = useMountRead(fetchClients);

    const handleFilterSelect = (filter: ClientFilter | null) => {
        const next = new URLSearchParams(searchParams);
        if (filter) {
            next.set("status", filter.key);
        } else {
            next.delete("status");
        }
        // Any filter change resets pagination.
        next.delete("page");
        setSearchParams(next);
    };

    // X2 Review: exactly the clients the banner counts, so the Pending chip
    // with no search (Lead call 2026-10-28).
    const handleReview = () => {
        const next = new URLSearchParams(searchParams);
        next.set("status", "pending");
        next.delete("search");
        next.delete("page");
        setSearchParams(next);
        // A search still in its debounce would otherwise be pushed back in.
        setSearchQuery("");
    };

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value);
    };

    // #333: a cleared sort (the third click) is the list's own order again.
    const handleSortingChange = (
        columnId: string | null,
        direction: "asc" | "desc" | null,
    ) => {
        const next = new URLSearchParams(searchParams);
        if (columnId && direction) {
            next.set("sortBy", columnId);
            next.set("sortDirection", direction);
        } else {
            next.delete("sortBy");
            next.delete("sortDirection");
        }
        // A new order makes the current page a different slice: start over.
        next.delete("page");
        setSearchParams(next);
    };

    const handlePageChange = (page: number) => {
        const next = new URLSearchParams(searchParams);
        next.set("page", page.toString());
        setSearchParams(next);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        const next = new URLSearchParams(searchParams);
        next.set("pageSize", newPageSize.toString());
        next.delete("page");
        setSearchParams(next);
    };

    return (
        <div className="px-10">
            <div className="flex items-center gap-4">
                <div className="grid flex-1 grid-cols-12 items-center gap-4">
                    <Title>Manage Clients</Title>
                    {/* X2: beside the title, as drawn. A failed read shows
                        its failure below, never a stale or zero count up
                        here. */}
                    {readStatus !== "failed" && (
                        <PendingClientsBanner
                            count={pendingCount}
                            onReview={handleReview}
                        />
                    )}
                </div>
                {/* C3: "+ New" as Clients.png draws it; its Import is not
                    MVP (C3: no client import). */}
                <Button
                    color={ComponentColor.Primary}
                    outline
                    onClick={() => setIsCreateOpen(true)}
                >
                    <Icon name="add" />
                    New
                    <span className="sr-only"> client</span>
                </Button>
            </div>
            {isCreateOpen && (
                <CreateClientDrawer
                    onClose={() => setIsCreateOpen(false)}
                    onCreated={() => void reload()}
                />
            )}

            {/* Mockup: search on the left, the filter chips on the right.
                StatusFilterChips carries its own mt-5; the search wrapper
                matches it, so the two centre on one line. */}
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="mt-5 w-full md:max-w-xl">
                    <Input
                        value={searchQuery}
                        name="clientSearch"
                        placeholder="Search clients..."
                        ariaLabel="Search clients by name or tax id"
                        icon={faMagnifyingGlass}
                        type="search"
                        fullWidth
                        onChange={handleSearch}
                    />
                </div>
                <StatusFilterChips
                    active={activeFilter}
                    onSelect={handleFilterSelect}
                />
            </div>

            {readStatus === "failed" ? (
                <ErrorState onRetry={reload} />
            ) : (
                <Table
                    data={items}
                    columns={columns}
                    size={ComponentSize.XS}
                    pagination={true}
                    isLoading={isFetching || readStatus === "loading"}
                    sortingProps={{
                        onSortingChange: handleSortingChange,
                        sorting: sort
                            ? [{ id: sort.columnId, desc: sort.desc }]
                            : [],
                    }}
                    paginationProps={{
                        currentPage: currentPage,
                        totalPages: totalPages,
                        pageSize: currentPageSize,
                        pageSizeOptions: [10, 25, 50, 100],
                        count: count,
                        allCount: allCount,
                        onPageChange: handlePageChange,
                        onPageSizeChange: handlePageSizeChange,
                        hasPreviousPage: hasPreviousPage,
                        hasNextPage: hasNextPage,
                    }}
                />
            )}
        </div>
    );
};
